import { useCallback, useMemo, useState } from 'react';
import { Member } from '../../api/groups';
import { ListEntity } from './GroupListHierarchy';
import uniqBy from 'lodash/uniqBy';
import { useFuzzySearch } from '../../hooks/useFuzzySearch';

function mapMember(member: Member): ListEntity {
  return {
    id: member.id,
    title: `${member.lastName} ${member.firstName}`,
  };
}

function addSelectedMembersToFilteredList(
  filteredList: List[],
  selectedMembersPerGroup: Record<string, string[]>,
  groupsById: Opts['groupsById'],
  membersPerGroup: Opts['membersPerGroup'],
) {
  const list = Object.entries(selectedMembersPerGroup).reduce((acc, [groupId, memberIds]) => {
    const selectedMembersOfGroup = selectedMembersPerGroup[groupId] || [];
    const allMembersOfGroup = membersPerGroup[groupId] || [];

    if (selectedMembersOfGroup.length === 0 || allMembersOfGroup.length === 0) return acc;

    const groupInList = acc.find(g => g.id === groupId);
    if (!groupInList) {
      const group = groupsById[groupId];

      acc.push({
        id: groupId,
        title: group.name,
        members: allMembersOfGroup.filter(m => memberIds.includes(m.id)).map(mapMember),
      });

      return acc;
    }

    groupInList.members.push(
      ...selectedMembersOfGroup.map(memberId => {
        const member = allMembersOfGroup.find(m => m.id === memberId)!;

        return mapMember(member);
      }),
    );
    groupInList.members = uniqBy(groupInList.members, 'id');
    groupInList.members.sort((a, b) => {
      const aIndex = allMembersOfGroup.findIndex(m => m.id === a.id);
      const bIndex = allMembersOfGroup.findIndex(m => m.id === b.id);

      return aIndex - bIndex;
    });

    return acc;
  }, filteredList);

  list.sort((a, b) => {
    const aIndex = Object.keys(groupsById).indexOf(a.id);
    const bIndex = Object.keys(groupsById).indexOf(b.id);

    return aIndex - bIndex;
  });

  return list;
}

type ListItems = ListEntity & {
  members: ListEntity[];
};

function createDefaultList({ membersPerGroup, groupsById }: Omit<Opts, 'selectedMembersPerGroup'>) {
  return Object.entries(membersPerGroup)
    .map(([groupId, members]) => {
      const group = groupsById[groupId];
      if (members.length === 0 || !group) return null;

      return {
        id: groupId,
        title: group.name,
        members: members.map(mapMember),
      };
    })
    .filter(Boolean) as ListItems[];
}

interface List extends ListEntity {
  members: ListEntity[];
}

interface Opts {
  membersPerGroup: Record<string, Member[]>;
  groupsById: Record<string, { id: string; name: string }>;
}

export function useBuildList({ membersPerGroup, groupsById }: Opts) {
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const listWithoutFilter = useMemo(() => {
    return createDefaultList({ membersPerGroup, groupsById });
  }, [groupsById, membersPerGroup]);

  const flattenedMembers = useMemo(() => {
    return uniqBy(
      listWithoutFilter.flatMap(g => g.members),
      'id',
    );
  }, [listWithoutFilter]);

  const searchGroups = useFuzzySearch(listWithoutFilter, 'title');
  const searchMembers = useFuzzySearch(flattenedMembers, 'title');

  const [filteredList, setFilteredList] = useState<List[]>(listWithoutFilter);

  const filterList = useCallback(
    (selectedMembersPerGroup: Record<string, string[]>, filterEnabled: boolean) => {
      if (!searchValue && !filterEnabled) {
        setFilteredList(listWithoutFilter);
        setSearchEnabled(false);
        return;
      }

      const listWithFilter = listWithoutFilter.reduce<List[]>((acc, group) => {
        const filteredMembers = searchMembers(searchValue).filter(m => group.members.some(gm => gm.id === m.id));
        const groupMatchesSearch = searchGroups(searchValue).some(g => g.id === group.id);

        if (groupMatchesSearch) {
          acc.push(group);
        } else if (filteredMembers.length > 0) {
          acc.push({
            ...group,
            members: filteredMembers,
          });
        }

        return acc;
      }, []);

      const filteredListWithSelectedMembers = addSelectedMembersToFilteredList(
        listWithFilter,
        selectedMembersPerGroup,
        groupsById,
        membersPerGroup,
      );

      setSearchEnabled(true);
      setFilteredList(filteredListWithSelectedMembers);
    },
    [groupsById, listWithoutFilter, membersPerGroup, searchGroups, searchMembers, searchValue],
  );

  function clearSearch() {
    setSearchValue('');
    setSearchEnabled(false);
    setFilteredList(listWithoutFilter);
  }

  return {
    isFilterEnabled: searchEnabled,
    list: filteredList,
    searchValue,
    setSearchValue,
    clearSearch,
    filterList,
  };
}
